/**
 * Analytics Tab Component for V4 Dashboard
 * Uses the new denormalized analytics endpoint for direct MUI chart integration
 */

import { useMemo, useState } from 'react'
import {
  Box,
  Card,
  Typography,
  Skeleton,
  Alert,
  Fade,
  Tabs,
  Tab,
} from '@mui/material'
import { BarChart, LineChart } from '@mui/x-charts'
import { nullSafeValueFormatter } from 'src/v3/utilities/charts'
import {
  BarChart as ChartIcon,
  QuizOutlined as QuizIcon,
} from '@mui/icons-material'
import { DashboardQueryParams } from '../../types'
import { useAnalyticsData } from '../../hooks/use-analytics-data'
import { QuizAnalytics } from '../../quiz-analytics'
import ExportTab from '../export/export-tab'

interface AnalyticsTabProps {
  queryParams: DashboardQueryParams
}

type ChartType = 'license' | 'user' | 'group'

/**
 * Capitalize category names for display
 */
function capitalizeCategory(category: string): string {
  return category
    .split('/')
    .map((word) =>
      word
        .split(/[\s-_]+/)
        .map(
          (subword) =>
            subword.charAt(0).toUpperCase() + subword.slice(1).toLowerCase(),
        )
        .join(' '),
    )
    .join('/')
}

/**
 * Learning Analytics Component (the existing chart functionality)
 */
function LearningAnalytics({ queryParams }: AnalyticsTabProps) {
  // Use React Query hook for data fetching
  let { data: chartData, isLoading, error } = useAnalyticsData(queryParams)

  // Determine chart type based on the actual data structure returned
  let chartType = useMemo((): ChartType => {
    if (!chartData || chartData.length === 0) {
      // Fallback to query params if no data yet
      if (queryParams.groupIDs && queryParams.groupIDs.length > 0) {
        return 'group'
      }
      if (queryParams.userIDs && queryParams.userIDs.length > 0) {
        return 'user'
      }
      return 'license'
    }

    // Detect from actual data structure
    let firstItem = chartData[0]
    if ('timeFrame' in firstItem) {
      return 'user' // Time series data
    }
    if ('groupName' in firstItem) {
      return 'group' // Group data
    }
    if ('licenseType' in firstItem) {
      return 'license' // License type data
    }

    // Default fallback
    return 'license'
  }, [chartData, queryParams.groupIDs, queryParams.userIDs])

  // Extract categories from the data
  let categories = useMemo(() => {
    if (!chartData || chartData.length === 0) return []

    let firstItem = chartData[0]
    return Object.keys(firstItem).filter(
      (key) => !['licenseType', 'timeFrame', 'groupName'].includes(key),
    )
  }, [chartData])

  let renderChart = () => {
    if (!chartData || chartData.length === 0) {
      let emptyMessage = (() => {
        switch (chartType) {
          case 'license':
            return 'No license type data available for chart'
          case 'user':
            return 'No time series data available. Please select users to view their CE trends over time.'
          case 'group':
            return 'No group data available for chart'
          default:
            return 'No data available for chart'
        }
      })()

      return (
        <Box
          sx={{
            height: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
          }}
        >
          <Typography>{emptyMessage}</Typography>
        </Box>
      )
    }

    try {
      switch (chartType) {
        case 'license':
        case 'group': {
          // Stacked bar chart for license types and groups
          let series = categories.map((category) => ({
            dataKey: category,
            label: capitalizeCategory(category),
            stack: 'categories',
            valueFormatter: nullSafeValueFormatter,
          }))

          let xAxisLabel = chartType === 'license' ? 'License Types' : 'Groups'
          let dataKey = chartType === 'license' ? 'licenseType' : 'groupName'

          return (
            <BarChart
              dataset={chartData}
              xAxis={[
                {
                  scaleType: 'band',
                  dataKey,
                  label: xAxisLabel,
                },
              ]}
              yAxis={[
                {
                  label: 'CE Hours',
                },
              ]}
              series={series}
              height={400}
              margin={{ left: 80, right: 20, top: 20, bottom: 100 }}
            />
          )
        }

        case 'user': {
          // Line chart for user time series
          let series = categories.map((category) => ({
            dataKey: category,
            label: capitalizeCategory(category),
            curve: 'linear' as const,
            valueFormatter: nullSafeValueFormatter,
          }))

          return (
            <LineChart
              dataset={chartData}
              xAxis={[
                {
                  scaleType: 'band' as const,
                  dataKey: 'timeFrame',
                  label: 'Time Period',
                },
              ]}
              yAxis={[
                {
                  label: 'CE Hours',
                },
              ]}
              series={series}
              height={400}
              margin={{ left: 80, right: 20, top: 20, bottom: 80 }}
            />
          )
        }

        default:
          return null
      }
    } catch (chartError) {
      let errorMessage = (() => {
        if (!(chartError instanceof Error)) {
          return 'Failed to render chart due to unexpected data format'
        }

        if (chartError.message.includes('dataKey')) {
          return 'Chart data is missing required fields for the selected view'
        }
        if (chartError.message.includes('dataset')) {
          return 'Chart data format is incompatible with the selected chart type'
        }
        return `Chart rendering failed: ${chartError.message}`
      })()

      return (
        <Box
          sx={{
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'error.main',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Chart Error
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try refreshing the page or selecting different filters
          </Typography>
        </Box>
      )
    }
  }

  let getChartTitle = () => {
    switch (chartType) {
      case 'license':
        return 'CE Hours by License Type & Category'
      case 'user':
        return 'CE Hours Over Time by Category (Selected Users)'
      case 'group':
        return 'CE Hours by Group & Category'
      default:
        return 'Advanced Analytics'
    }
  }

  let getChartDescription = () => {
    switch (chartType) {
      case 'license':
        return 'Stacked bar chart showing CE hours for each license type, broken down by CE categories. Users with multiple licenses are counted per license.'
      case 'user':
        return 'Time series showing how CE hours in each category have changed over months for the selected users'
      case 'group':
        return 'Stacked bar chart showing CE hours for each selected group, broken down by CE categories'
      default:
        return 'Interactive charts showing CE activity patterns and trends'
    }
  }

  if (error) {
    let errorMessage = (() => {
      if (!(error instanceof Error)) {
        return 'Unable to load analytics data from the server'
      }

      if (error.message.includes('404')) {
        return 'Analytics endpoint not found. Please check your configuration.'
      }
      if (error.message.includes('401') || error.message.includes('403')) {
        return 'You do not have permission to access analytics data.'
      }
      if (error.message.includes('500')) {
        return 'Server error occurred while processing analytics request.'
      }
      if (error.message.includes('timeout')) {
        return 'Request timed out. The server may be experiencing high load.'
      }
      if (error.message.includes('Network')) {
        return 'Network error. Please check your internet connection.'
      }
      return `Failed to load analytics data: ${error.message}`
    })()

    return (
      <Card sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Analytics Data Error
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {errorMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Try refreshing the page or adjusting your filters. If the problem
            persists, contact support.
          </Typography>
        </Alert>
      </Card>
    )
  }

  return (
    <Card sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <ChartIcon />
          {getChartTitle()}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 600 }}
        >
          {getChartDescription()}
        </Typography>
      </Box>

      {isLoading ? (
        <Skeleton variant="rectangular" height={400} />
      ) : !chartData || chartData.length === 0 ? (
        <Box
          sx={{
            height: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          <ChartIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No Data Available
          </Typography>
          <Typography variant="body2">
            No CE activities found for the selected criteria. Try adjusting your
            filters or date range.
          </Typography>
        </Box>
      ) : (
        <Fade in={!isLoading} timeout={1000}>
          <Box>{renderChart()}</Box>
        </Fade>
      )}
    </Card>
  )
}

/**
 * Main Analytics Tab Component with Tabs
 */
export default function AnalyticsTab({ queryParams }: AnalyticsTabProps) {
  let [activeTab, setActiveTab] = useState(0)

  let handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  return (
    <Box>
      {/* Tab Navigation */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0,
        }}
      >
        <Tab
          icon={<ChartIcon />}
          label="Learning"
          iconPosition="start"
          sx={{ minHeight: 48 }}
        />
        <Tab
          icon={<QuizIcon />}
          label="Quiz Analytics"
          iconPosition="start"
          sx={{ minHeight: 48 }}
        />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && <LearningAnalytics queryParams={queryParams} />}
      {activeTab === 1 && (
        <Card sx={{ p: 3 }}>
          <QuizAnalytics
            startDate={queryParams.startDate}
            endDate={queryParams.endDate}
          />
        </Card>
      )}
      {activeTab === 2 && <ExportTab queryParams={queryParams} />}
    </Box>
  )
}
